'use client'

import { Combobox, ComboboxButton, ComboboxInput, ComboboxOption, Label } from '@headlessui/react'
import { ChevronDown, Check, Search, Eye, Edit, Crown, Shield, UserCheck, Users, Building, MapPin, Folder, Shapes, CircleSlash } from 'lucide-react'
import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { Avatar } from '../avatar/Avatar'
import { LoadingSpinner } from './LoadingSpinner'
import { useFloating, offset, flip, shift, size, autoUpdate } from '@floating-ui/react-dom';
import { createPortal } from 'react-dom';
import { normalizeRoleForDisplay } from '@airclou/utils';

export interface ComboBoxItem {
  id: string
  name: string
  email?: string
  imageUrl?: string
  emoji?: string
  role?: string
  department?: string
  type?: 'user' | 'group' | 'role' | 'team' | 'department' | 'location' | 'other'
  permissionLevel?: string // Can be any role value like 'Admin', 'Viewer', etc.
  groupType?: 'role' | 'team' | 'department' | 'location' | 'other'
  [key: string]: any
}

interface ComboBoxResponse {
  items: ComboBoxItem[]
  total: number
  hasMore: boolean
}

interface ComboBoxProps {
  // Data fetching
  fetchItems: (query: string, limit: number, offset: number, exclude?: string[]) => Promise<ComboBoxResponse>
  
  // Selection
  selectedItems?: ComboBoxItem[]
  onSelectionChange?: (items: ComboBoxItem[]) => void
  multiSelect?: boolean
  
  // Exclusions
  excludeItems?: ComboBoxItem[]
  
  // UI props
  label?: string
  placeholder?: string
  className?: string
  disabled?: boolean
  id?: string
  
  // Performance
  pageSize?: number
  debounceMs?: number
  
  // Styling
  maxHeight?: string
  zIndex?: number
  
  // Icon mappings
  permissionLevelIconMap?: Record<string, React.ComponentType<{ className?: string }>>
  groupTypeIconMap?: Record<string, React.ComponentType<{ className?: string }>>
}

// Custom hook for debounced values
function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value)

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value)
    }, delay)

    return () => {
      clearTimeout(handler)
    }
  }, [value, delay])

  return debouncedValue
}

// Helper function to get permission level icon
function getPermissionLevelIcon(permissionLevel?: string, iconMap?: Record<string, React.ComponentType<{ className?: string }>>) {
  if (iconMap && permissionLevel && iconMap[permissionLevel]) {
    return iconMap[permissionLevel];
  }
  
  // Fallback to default mapping
  switch (permissionLevel) {
    case 'billing_admin': return Crown
    case 'admin': return Shield
    case 'edit': return Edit
    case 'view': return Eye
    default: return UserCheck
  }
}

// Helper function to get group type icon
function getGroupTypeIcon(groupType?: string, iconMap?: Record<string, React.ComponentType<{ className?: string }>>) {
  if (iconMap && groupType && iconMap[groupType]) {
    return iconMap[groupType];
  }
  
  // Fallback to default mapping
  switch (groupType) {
    case 'role': return UserCheck
    case 'team': return Users
    case 'department': return Building
    case 'location': return MapPin
    case 'other': return Shapes
    default: return Folder
  }
}

export function ComboBox({
  fetchItems,
  selectedItems = [],
  onSelectionChange,
  multiSelect = false,
  excludeItems = [],
  label,
  placeholder = "Search...",
  className = "",
  disabled = false,
  pageSize = 20,
  debounceMs = 300,
  maxHeight = "14rem", // 224px
  zIndex = 9999,
  permissionLevelIconMap,
  groupTypeIconMap,
  id
}: ComboBoxProps) {
  const [query, setQuery] = useState('')
  const [items, setItems] = useState<ComboBoxItem[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isInputFocused, setIsInputFocused] = useState(false);
  const [hasEditedAfterSelection, setHasEditedAfterSelection] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const lastFetchedQuery = useRef<string>('');
  const isSelectingRef = useRef(false);
  const activeRequestRef = useRef<AbortController | null>(null);
  const floating = useFloating({
    open: isDropdownOpen,
    middleware: [
      offset(4), 
      flip(), 
      shift(),
      size({
        apply({ availableHeight, elements }) {
          Object.assign(elements.floating.style, {
            maxHeight: `min(${availableHeight}px, ${maxHeight})`,
          });
        },
        padding: 8
      })
    ],
    whileElementsMounted: autoUpdate,
    placement: 'bottom-start',
  });

  const debouncedQuery = useDebounce(query, debounceMs);

  // Use refs to store values that shouldn't trigger re-renders
  const fetchItemsRef = useRef(fetchItems)
  const pageSizeRef = useRef(pageSize)
  const excludeItemsRef = useRef(excludeItems)
  const onSelectionChangeRef = useRef(onSelectionChange)
  const selectedItemsRef = useRef(selectedItems)
  
  // Update refs when props change
  useEffect(() => {
    fetchItemsRef.current = fetchItems
  }, [fetchItems])
  
  useEffect(() => {
    pageSizeRef.current = pageSize
  }, [pageSize])
  
  useEffect(() => {
    excludeItemsRef.current = excludeItems
  }, [excludeItems])
  
  useEffect(() => {
    onSelectionChangeRef.current = onSelectionChange
  }, [onSelectionChange])
  
  useEffect(() => {
    selectedItemsRef.current = selectedItems
    // Reset edit flag when selected items change externally
    setHasEditedAfterSelection(false)
  }, [selectedItems])
  
  // Removed shouldShowLoading - just use loading state directly

  // Fetch items function - now with no external dependencies
  const performFetch = useCallback(async (searchQuery: string) => {
    if (searchQuery.length < 2) {
      setItems([])
      lastFetchedQuery.current = ''
      setLoading(false)
      return
    }

    // Prevent refetching the same query
    if (lastFetchedQuery.current === searchQuery) {
      setLoading(false)
      return
    }

    // Skip fetch if we're currently selecting items
    if (isSelectingRef.current) {
      return
    }

    // Cancel any previous request
    if (activeRequestRef.current) {
      activeRequestRef.current.abort()
    }

    try {
      setError(null)
      setLoading(true)
      
      // Create new abort controller for this request
      const abortController = new AbortController()
      activeRequestRef.current = abortController

      // Do NOT exclude selected items from fetch
      const response = await fetchItemsRef.current(searchQuery, pageSizeRef.current, 0)
      
      // Check if this request was aborted
      if (abortController.signal.aborted) {
        return
      }
      
      // Only update state if this is still the active request
      if (activeRequestRef.current === abortController) {
        setItems(response.items)
        lastFetchedQuery.current = searchQuery
        activeRequestRef.current = null
      }
    } catch (err) {
      // Ignore aborted requests
      if (err instanceof Error && err.name === 'AbortError') {
        return
      }
      
      console.error('ComboBox fetch error:', err)
      setError(err instanceof Error ? err.message : 'Failed to fetch items')
      setItems([])
      // Don't update lastFetchedQuery on error so it can be retried
    } finally {
      setLoading(false)
    }
  }, []) // Empty dependency array - the function never changes

  // Effect for debounced search - use a ref to prevent unnecessary re-fetches
  useEffect(() => {
    if (!isSelectingRef.current) {
      performFetch(debouncedQuery)
    }
  }, [debouncedQuery, performFetch])

  // Handle selection for single-select mode
  const handleSingleSelection = useCallback((item: ComboBoxItem | null) => {
    // When clicking outside or pressing Escape, HeadlessUI passes null
    if (!item) {
      // Don't call the handler when dismissing without selection
      return;
    }
    onSelectionChangeRef.current?.([item])
    setQuery('')
    setHasEditedAfterSelection(false) // Reset the edit flag on new selection
  }, [])

  // Handle selection for multi-select mode
  const handleMultiSelection = useCallback((item: ComboBoxItem, event: React.MouseEvent) => {
    event.preventDefault()
    event.stopPropagation()
    
    isSelectingRef.current = true
    
    const currentSelectedItems = selectedItemsRef.current
    const isSelected = currentSelectedItems.some(selected => selected.id === item.id)
    
    if (isSelected) {
      // Remove from selection
      const newSelection = currentSelectedItems.filter(selected => selected.id !== item.id)
      onSelectionChangeRef.current?.(newSelection)
    } else {
      // Add to selection
      onSelectionChangeRef.current?.([...currentSelectedItems, item])
    }
    
    // Reset the flag after a short delay to allow for re-renders
    setTimeout(() => {
      isSelectingRef.current = false
    }, 100)
  }, [])

  // Display value for single select - modified to handle editing state
  const displayValue = useMemo(() => {
    if (multiSelect) {
      return query
    }
    // If user has edited after selection and is focused, always show query
    if (hasEditedAfterSelection && isInputFocused) {
      return query
    }
    // Otherwise show query if it exists, or selected item name
    return query || (selectedItems.length > 0 ? selectedItems[0].name : '')
  }, [multiSelect, query, selectedItems, hasEditedAfterSelection, isInputFocused])

  // Add click outside handler for dropdown/input
  useEffect(() => {
    if (!isDropdownOpen) return;
    function handlePointerDown(event: MouseEvent) {
      const inputEl = inputRef.current;
      const dropdownEl = floating.refs.floating.current;
      if (
        inputEl && !inputEl.contains(event.target as Node) &&
        dropdownEl && !dropdownEl.contains(event.target as Node)
      ) {
        setIsDropdownOpen(false);
        setIsInputFocused(false);
        setHasEditedAfterSelection(false); // Reset edit flag on click outside
      }
    }
    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [isDropdownOpen, floating.refs.floating]);

  // Open dropdown when input is focused or user is typing
  useEffect(() => {
    if (isInputFocused) {
      setIsDropdownOpen(true);
    }
  }, [isInputFocused, query]);

  return (
    <div className={className} ref={floating.refs.setReference}>
      <Combobox
        value={multiSelect ? null : selectedItems[0] || null}
        onChange={multiSelect ? () => {} : handleSingleSelection}
        disabled={disabled}
      >
        {label && (
          <Label htmlFor={id} className="block text-sm/6 font-medium text-foreground">
            {label}
          </Label>
        )}
        <div className="relative mt-2">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 size-4 text-gray-400 pointer-events-none" aria-hidden="true" />
          <ComboboxInput
            ref={(node) => {
              floating.refs.setReference(node);
              ;(inputRef as React.MutableRefObject<HTMLInputElement | null>).current = node as HTMLInputElement | null;
            }}
            id={id}
            name={id ? `${id}-input` : 'combobox-input'}
            className="w-full border border-field rounded-md bg-background dark:bg-white/5 text-gray-900 dark:text-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent field-text transition-colors py-2 pl-10 pr-12 disabled:opacity-50"
            style={{ height: '38px' }}
            onChange={(event) => {
              setQuery(event.target.value)
              setHasEditedAfterSelection(true) // Mark that user has edited
              setIsDropdownOpen(true)
            }}
            onFocus={() => {
              setIsInputFocused(true)
              setIsDropdownOpen(true)
            }}
            onBlur={() => {
              setIsInputFocused(false)
              setHasEditedAfterSelection(false) // Reset edit flag on blur
              // Do not close dropdown here; let click outside handler manage it
            }}
            displayValue={() => displayValue}
            placeholder={placeholder}
            disabled={disabled}
            autoComplete="off"
          />
          <ComboboxButton className="absolute inset-y-0 right-0 flex items-center rounded-r-md px-2 focus:outline-none">
            <ChevronDown 
              className={`size-5 text-gray-400 transition-transform duration-200 ${
                isDropdownOpen ? 'rotate-180' : 'rotate-0'
              }`} 
              aria-hidden="true" 
            />
          </ComboboxButton>

          {/* Render dropdown in portal for overflow, using Floating UI for positioning */}
          {isDropdownOpen && createPortal(
            <div
              ref={floating.refs.setFloating}
              className="overflow-auto rounded-md bg-white dark:bg-gray-800 py-1 text-base shadow-lg ring-1 ring-black/5 dark:ring-white/5 focus:outline-none sm:text-sm"
              data-combobox-dropdown="true"
              style={{
                position: floating.strategy,
                top: floating.y ?? 0,
                left: floating.x ?? 0,
                width: inputRef.current ? (inputRef.current as HTMLElement).offsetWidth : undefined,
                zIndex,
              }}
            >
              {/* Multi-select header */}
              {multiSelect && selectedItems.length > 0 && (
                <div className="sticky top-0 z-10 px-3 py-2 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-primary font-medium">
                      {selectedItems.length} selected
                    </span>
                    <button
                      onClick={(e) => {
                        e.preventDefault()
                        e.stopPropagation()
                        onSelectionChange?.([])
                        setHasEditedAfterSelection(false) // Reset edit flag
                      }}
                      className="text-muted-foreground hover:text-foreground text-sm"
                    >
                      Clear all
                    </button>
                  </div>
                </div>
              )}
              {/* Loading state */}
              {loading && (
                <div className="flex items-center justify-center py-8">
                  <LoadingSpinner />
                </div>
              )}
              {/* Error state */}
              {error && !loading && (
                <div className="p-4 text-center text-red-600 dark:text-red-400">
                  <p className="text-sm">{error}</p>
                </div>
              )}
              {/* Items list */}
              {!loading && !error && items.length > 0 && (
                items.map((item) => {
                  const isSelected = selectedItems.some(selected => selected.id === item.id)
                  if (multiSelect) {
                    return (
                      <div
                        key={item.id}
                        className={`group cursor-pointer select-none relative py-2 pl-3 pr-9 text-foreground hover:bg-primary hover:text-primary-foreground ${
                          isSelected ? 'bg-primary/5 dark:bg-primary/10' : ''
                        }`}
                        onClick={(event) => {
                          event.preventDefault()
                          event.stopPropagation()
                          handleMultiSelection(item, event)
                        }}
                      >
                        <div className="flex items-center">
                          {item.emoji ? (
                            <span className="text-xl shrink-0 w-6 h-6 flex items-center justify-center">{item.emoji}</span>
                          ) : item.type === 'group' ? (
                            <div className="size-6 shrink-0 rounded-md bg-primary/15 dark:bg-primary/25 flex items-center justify-center text-xs font-medium text-primary">
                              {item.name.split(' ').map(word => word[0]).join('').toUpperCase().slice(0, 2)}
                            </div>
                          ) : (
                            <Avatar
                              name={item.name}
                              src={item.imageUrl}
                              size="sm"
                            />
                          )}
                          <div className="ml-3 flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              {/* Permission level icon for users */}
                              {item.type === 'user' && item.permissionLevel && (() => {
                                const PermissionIcon = getPermissionLevelIcon(item.permissionLevel, permissionLevelIconMap)
                                return <PermissionIcon className="h-3 w-3 text-gray-500 dark:text-gray-400 group-hover:text-white" />
                              })()}
                              
                              {/* Group type icon for groups */}
                              {(item.type === 'group' || item.groupType) && (() => {
                                const GroupIcon = getGroupTypeIcon(item.groupType || item.type, groupTypeIconMap)
                                return <GroupIcon className="h-3 w-3 text-gray-500 dark:text-gray-400 group-hover:text-white" />
                              })()}
                              
                              <span className="block truncate font-medium">{item.name}</span>
                            </div>
                            {item.email && (
                              <span className="block truncate text-sm text-gray-500 group-hover:text-primary-foreground/75">
                                {item.email}
                              </span>
                            )}
                            {item.role && (
                              <span className="block truncate text-sm text-gray-500 group-hover:text-primary-foreground/75">
                                {normalizeRoleForDisplay(item.role)}
                              </span>
                            )}
                          </div>
                        </div>
                        {isSelected && (
                          <Check className="absolute right-3 top-1/2 transform -translate-y-1/2 size-5 text-primary group-hover:text-primary-foreground" />
                        )}
                      </div>
                    )
                  } else {
                    return (
                      <ComboboxOption
                        key={item.id}
                        value={item}
                        className="group cursor-pointer select-none relative py-2 pl-3 pr-9 text-foreground hover:bg-primary hover:text-primary-foreground data-[focus]:bg-primary data-[focus]:text-primary-foreground data-[focus]:outline-none"
                      >
                        <div className="flex items-center">
                          {item.emoji ? (
                            <span className="text-xl shrink-0 w-6 h-6 flex items-center justify-center">{item.emoji}</span>
                          ) : item.type === 'group' ? (
                            <div className="size-6 shrink-0 rounded-md bg-primary/15 dark:bg-primary/25 flex items-center justify-center text-xs font-medium text-primary">
                              {item.name.split(' ').map(word => word[0]).join('').toUpperCase().slice(0, 2)}
                            </div>
                          ) : (
                            <Avatar
                              name={item.name}
                              src={item.imageUrl}
                              size="sm"
                            />
                          )}
                          <div className="ml-3 flex-1 min-w-0">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                {/* Permission level icon for users */}
                                {item.type === 'user' && item.permissionLevel && (() => {
                                  const PermissionIcon = getPermissionLevelIcon(item.permissionLevel, permissionLevelIconMap)
                                  return <PermissionIcon className="h-3 w-3 text-gray-500 dark:text-gray-400 group-hover:text-white group-data-[focus]:text-white" />
                                })()}
                                
                                {/* Group type icon for groups */}
                                {(item.type === 'group' || item.groupType) && (() => {
                                  const GroupIcon = getGroupTypeIcon(item.groupType || item.type, groupTypeIconMap)
                                  return <GroupIcon className="h-3 w-3 text-gray-500 dark:text-gray-400 group-hover:text-white group-data-[focus]:text-white" />
                                })()}
                                
                                <span className="block truncate font-medium">{item.name}</span>
                              </div>
                            </div>
                            {item.email && (
                              <span className="block truncate text-sm text-gray-500 group-hover:text-primary-foreground/75 group-data-[focus]:text-primary-foreground/75">
                                {item.email}
                              </span>
                            )}
                            {item.role && (
                              <span className="block truncate text-sm text-gray-500 group-hover:text-primary-foreground/75 group-data-[focus]:text-primary-foreground/75">
                                {normalizeRoleForDisplay(item.role)}
                              </span>
                            )}
                          </div>
                        </div>
                      </ComboboxOption>
                    )
                  }
                })
              )}
              {/* No results */}
              {!loading && !error && items.length === 0 && debouncedQuery.length >= 2 && lastFetchedQuery.current === debouncedQuery && (
                <div className="flex items-center justify-center py-8">
                  <div className="text-center text-gray-500 dark:text-gray-400">
                    <CircleSlash className="h-6 w-6 mx-auto mb-2 text-gray-400" />
                    <p className="text-sm">No items found for "{debouncedQuery}"</p>
                  </div>
                </div>
              )}
              {/* Search prompt */}
              {!loading && !error && debouncedQuery.length < 2 && items.length === 0 && (
                <div className="flex items-center justify-center py-8">
                  <div className="text-center text-gray-500 dark:text-gray-400">
                    <p className="text-sm">Type at least 2 characters to search</p>
                  </div>
                </div>
              )}
            </div>,
            document.body
          )}
        </div>
      </Combobox>
    </div>
  )
}